/**
 * Sections API — Manual section-moderator grant (plan 2026-06-30-001).
 *
 * Two endpoints share these contracts:
 *  - PATCH /v1/sections/:section_id/members/grant   (U5: grant / revoke)
 *  - GET   /v1/sections/:section_id/members          (U5b: grantor-gated roster)
 *
 * Both web (admin) and mobile (section leader) call the same write endpoint, so
 * the grant-authorization primitive is shared structurally (R17).
 */

import { z } from 'zod'

// ─── PATCH /v1/sections/:section_id/members/grant ────────────────────────────

export const SectionMemberGrantRequestSchema = z.object({
  /** The target school user's publicId (UsersOnSchools.publicId). */
  userId: z.string().min(1),
  /** true = grant moderator; false = revoke. */
  grant: z.boolean()
})
export type SectionMemberGrantRequest = z.infer<typeof SectionMemberGrantRequestSchema>

/** A section member with identity + grant status, as returned by both endpoints. */
export const SectionMemberGrantEntrySchema = z.object({
  /** UsersOnSchools.publicId. */
  userId: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  /** Distinct identity roles the member holds in the section (STUDENT/PARENT/...). */
  roles: z.array(z.string()),
  /** Sync-derived leadership (Blackbaud). */
  isLeader: z.boolean(),
  isHeadRole: z.boolean(),
  /** Manual moderator grant flag (member-level). */
  manualGrant: z.boolean(),
  /** Derived effective moderator: isLeader || isHeadRole || manualGrant. */
  canModerate: z.boolean(),
  /**
   * Audit (R19). When the member currently HOLDS the grant, `grantedAt`/`grantedBy`
   * carry the last grant's who/when and the revoke fields are null. When they do
   * NOT, `revokedAt`/`revokedBy` carry the most-recent revoke (system-actor
   * `SYSTEM_ROSTER_SYNC` for a roster-driven clear-on-removal) and the grant fields
   * are null. All four are null for a member who was never granted.
   */
  grantedAt: z.string().nullable(),
  grantedBy: z.string().nullable(),
  revokedAt: z.string().nullable(),
  revokedBy: z.string().nullable()
})
export type SectionMemberGrantEntry = z.infer<typeof SectionMemberGrantEntrySchema>

export const SectionMemberGrantResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    member: SectionMemberGrantEntrySchema,
    /**
     * Best-effort Stream chat propagation outcome (KTD9). `false` means the DB
     * grant committed but the immediate chat role update failed — the next full
     * channel re-sync reconciles it. The grant itself always persisted.
     */
    chatPropagated: z.boolean()
  })
})
export type SectionMemberGrantResponse = z.infer<typeof SectionMemberGrantResponseSchema>

// ─── GET /v1/sections/:section_id/members ────────────────────────────────────

export const ListSectionMembersResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    members: z.array(SectionMemberGrantEntrySchema),
    /**
     * Per-section server signal (R20 / OQ7): whether the viewer may grant/revoke
     * on THIS section. Clients show/hide the grant affordance off this, never by
     * inferring from the viewer's general leader status.
     */
    viewerCanGrant: z.boolean()
  })
})
export type ListSectionMembersResponse = z.infer<typeof ListSectionMembersResponseSchema>
